import { FC, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../../store/slice';

// PACKAGES
import { Spinner } from 'react-bootstrap';

// COMPONENTS
import PaginationCustom from '../../Pagination/Pagination';
import ScrollContainer from 'react-indiana-drag-scroll';
import ScrollButtons from './ScrollButtons';
import DelayedFadeIn from '../../_Helpers/DelayedFadeIn';
import SortButton from './SortButton';
import StudentProgressCell from './StudentProgressCell';
import SvgMask from '../../_Helpers/SvgMask';

// UTILS
import { guid } from '../../../libs/utils';
import { ToastMessagesSlice } from '../../../store/slice/ToastMessages';

// TYPES
import apibridge, { GetAssessmentOverviewTypes } from '../../../apibridge';
import { AssessmentSummaryProgressProgressInfo } from '../../../api/models';

interface StudentProgressTableProps {
	assessmentStudentProgressParams: GetAssessmentOverviewTypes['StudentProgressType'];
	setAssessmentStudentProgressParams: React.Dispatch<
		React.SetStateAction<GetAssessmentOverviewTypes['StudentProgressType']>
	>;
	onStudentClick: (classId: string, studentId: string) => Promise<void>;
}

const StudentProgressTable: FC<StudentProgressTableProps> = ({
	assessmentStudentProgressParams,
	setAssessmentStudentProgressParams,
	onStudentClick
}) => {
	const dispatch = useAppDispatch();

	const [isLoading, setIsLoading] = useState(true);
	const [isReloading, setIsReloading] = useState(false);

	const [tableData, setTableData] = useState<AssessmentSummaryProgressProgressInfo>();
	const [stageColWidth, setStageColWidth] = useState<number>(0);

	const [assessmentGroupWidth, setAssessmentGroupWidth] = useState<number>(0);
	const [individualCellWidth, setIndividualCellWidth] = useState<number>(0);

	const {
		assessmentInfos = [],
		assessmentPeriods = [],
		stages = [],
		students = [],
		currentPage = 0,
		itemsPerPage = 0,
		totalItems = 0,
		totalPages = 0
	} = tableData || {};

	// Scroll Logic
	const [scrollPosition, setScrollPosition] = useState(0);
	const [scrollWidth, setScrollWidth] = useState(0);
	const [currentStageIndex, setCurrentStageIndex] = useState(0);

	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const tableContainerRef = useRef<HTMLDivElement>(null);
	const previousParamsRef = useRef<string | null>(null);

	// Constants
	const STUDENT_COL_WIDTH = 320; // Fixed width for student column

	const calculateWidths = () => {
		if (tableContainerRef.current) {
			const containerWidth = tableContainerRef.current.clientWidth;
			const availableWidth = containerWidth - STUDENT_COL_WIDTH;
			setStageColWidth(availableWidth);
			setAssessmentGroupWidth(availableWidth);

			// Calculate individual cell width based on number of assessment periods
			if (assessmentPeriods.length > 0) {
				const cellWidth = availableWidth / assessmentPeriods.length;
				setIndividualCellWidth(cellWidth);
			}
		}
	};

	const scrollToStage = (stageIndex: number) => {
		if (scrollContainerRef.current && stageColWidth > 0) {
			const newScrollPosition = stageIndex * stageColWidth;
			scrollContainerRef.current.scrollLeft = newScrollPosition;
			setScrollPosition(newScrollPosition);
			setCurrentStageIndex(stageIndex);
		}
	};

	const scrollToNextStage = () => {
		if (currentStageIndex < assessmentInfos.length - 1) {
			scrollToStage(currentStageIndex + 1);
		}
	};

	const scrollToPreviousStage = () => {
		if (currentStageIndex > 0) {
			scrollToStage(currentStageIndex - 1);
		}
	};

	const getTableData = async () => {
		setIsReloading(true);

		const response = await apibridge.getAssessmentSummaryProgress(assessmentStudentProgressParams);
		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setTableData(response.data.result);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Student progress error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsLoading(false);
		setIsReloading(false);
	};

	useEffect(() => {
		if (!isLoading && scrollContainerRef.current) {
			const containerWidth = scrollContainerRef.current.clientWidth;
			const contentWidth = scrollContainerRef.current.scrollWidth;
			const maxScroll = contentWidth - containerWidth;
			setScrollWidth(maxScroll);
			// Reset to first stage when data changes
			setCurrentStageIndex(0);
			setScrollPosition(0);
		}
	}, [isLoading]);

	useEffect(() => {
		const serializedParams = JSON.stringify(assessmentStudentProgressParams);

		if (previousParamsRef.current === serializedParams) {
			return;
		}

		previousParamsRef.current = serializedParams;

		getTableData();
	}, [assessmentStudentProgressParams]); // eslint-disable-line react-hooks/exhaustive-deps

	// Calculate stage column width when assessment data changes or component mounts
	useEffect(() => {
		if (!isLoading) {
			calculateWidths();
		}
	}, [isLoading, assessmentPeriods.length]);

	// Handle window resize
	useEffect(() => {
		const handleResize = () => {
			if (!isLoading) {
				calculateWidths();
			}
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [isLoading, assessmentPeriods.length]);

	// Reset page to 1 when params change, otherwise pagination will break
	useEffect(() => {
		if ((assessmentStudentProgressParams.pageNumber || 1) !== 1) {
			setAssessmentStudentProgressParams({ ...assessmentStudentProgressParams, pageNumber: 1 });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [assessmentStudentProgressParams._class]);

	return (
		<div className="assessment-general-table stage-overview-table student-progress-table">
			{isLoading ? (
				<div className="d-flex align-items-center justify-content-center position-relative w-100">
					<DelayedFadeIn>
						<Spinner animation="border" role="status">
							<span className="visually-hidden">Loading...</span>
						</Spinner>
					</DelayedFadeIn>
				</div>
			) : (
				<>
					<div ref={tableContainerRef} className={`table-container text-shades-800 ${isReloading ? 'opacity-75' : ''}`}>
						<div className="table-col-students">
							<div className="student-row-heading border-bottom-0 w-100"></div>

							<div className="student-row-heading student-progress-height-offset w-100">
								<div className="student-name-cell d-flex align-items-center justify-content-between gap-3 w-100">
									<span className="body-tiny">
										<strong>Student name</strong>
									</span>

									<SortButton
										isProgressTable={true}
										type="Student"
										params={assessmentStudentProgressParams}
										setParams={setAssessmentStudentProgressParams}
										disabled={isReloading}
									/>
								</div>
							</div>

							{/* Student name List */}
							{students.length > 0 ? (
								<div className="student-buttons-wrapper">
									{students.map((student, studentIndex) => {
										return (
											<button
												className={`btn btn-student student-row d-flex justify-content-between ${
													studentIndex % 2 === 0 ? 'bg-white' : ''
												}`}
												key={studentIndex}
												onClick={() => onStudentClick(student.classId || '', student.id || '')}
											>
												<div className="student-name-cell d-flex flex-column justify-content-center">
													<span className="student-name body-small">
														<strong>
															{student.firstName} {student.lastName}
														</strong>
													</span>

													<span className="body-mini fw-semibold opacity-50">{student.className}</span>
												</div>

												<div className="last-recorded-cell justify-content-end">
													<div className="edit-btn">
														<SvgMask path="/svg/edit.svg" width={24} height={24} />
													</div>
												</div>
											</button>
										);
									})}
								</div>
							) : (
								<div className="student-row d-flex justify-content-between">
									<div className="student-name-cell d-flex flex-column justify-content-center">
										<p className="my-3">No results</p>
									</div>
								</div>
							)}
						</div>

						{/* Scroll Container */}
						<div className="table-col-content d-flex scroll-btn-anchor w-100">
							<ScrollButtons
								scrollPosition={scrollPosition}
								setScrollPosition={setScrollPosition}
								scrollWidth={scrollWidth}
								scrollContainerRef={scrollContainerRef}
								onNext={scrollToNextStage}
								onPrevious={scrollToPreviousStage}
								canGoNext={currentStageIndex < assessmentInfos.length - 1}
								canGoPrevious={currentStageIndex > 0}
							/>
							<ScrollContainer
								className="stages-scroll-container w-100"
								hideScrollbars={true}
								vertical={false}
								horizontal={false}
								innerRef={scrollContainerRef}
								onScroll={() => {
									if (scrollContainerRef.current) {
										setScrollPosition(scrollContainerRef.current.scrollLeft);
									}
								}}
							>
								{/* Header */}
								<div className="d-flex stage-header-container">
									{assessmentInfos.map((info, infoIndex) => {
										const { key, label } = info;

										return (
											<div
												key={infoIndex}
												className="stage-col border-bottom-0 border-end-0"
												style={{ width: `${stageColWidth}px` }}
											>
												<div className="stage-name no-border-right justify-content-center px-2">
													<span className="body-small fw-bold text-shades-800">
														{label?.toLowerCase() === 'gpk' ? 'Grapheme-Phoneme Knowledge' : label}
													</span>
												</div>

												<div className="d-flex">
													{assessmentPeriods?.length > 0 &&
														assessmentPeriods.map((period, periodIndex) => {
															return (
																<div
																	key={info.key + '-' + period.key}
																	className="stage-name progress-name-wrapper student-progress-height-offset no-border-right px-2"
																	style={{ width: `${individualCellWidth}px` }}
																>
																	<span className="body-tiny fw-semibold text-shades-800">{period.yearLabel}</span>

																	<span className="body-tiny fw-bold text-shades-800">{period.semesterLabel}</span>
																</div>
															);
														})}
												</div>
											</div>
										);
									})}
								</div>

								{students.length > 0 ? (
									<div className="table-row-wrapper">
										{students.map((student, studentIndex) => {
											const zebraStriped = studentIndex % 2 === 0 ? 'alternate-bg-white' : '';

											return (
												<div key={studentIndex} className="table-row student-row d-flex w-100 border-end-0">
													{/* GPK */}
													<div
														className={`assessment-student-group ${zebraStriped}`}
														style={{ width: `${assessmentGroupWidth}px` }}
													>
														{student?.graphemePhoneme &&
															student.graphemePhoneme.length > 0 &&
															student.graphemePhoneme.map((assessmentType, assessmentTypeIndex) => {
																const isFirst = assessmentTypeIndex === 0;
																const isLast = assessmentTypeIndex === (student.graphemePhoneme?.length || 0) - 1;

																const stageId = assessmentType.stageId;
																const stageName = stages.find((stage) => stage.id === stageId)?.shortName || '-';

																return (
																	<StudentProgressCell
																		key={`gpk-${assessmentType.assessmentPeriodId}-${assessmentType.stageId}`}
																		stageName={stageName}
																		width={individualCellWidth}
																		hideLeftLine={isFirst}
																		hideRightLine={isLast}
																	/>
																);
															})}
													</div>

													{/* Reading */}
													<div
														className={`assessment-student-group ${zebraStriped}`}
														style={{ width: `${assessmentGroupWidth}px` }}
													>
														{student?.reading &&
															student.reading.length > 0 &&
															student.reading.map((assessmentType, assessmentTypeIndex) => {
																const isFirst = assessmentTypeIndex === 0;
																const isLast = assessmentTypeIndex === (student.graphemePhoneme?.length || 0) - 1;

																const stageId = assessmentType.stageId;
																const stageName = stages.find((stage) => stage.id === stageId)?.shortName || '-';

																return (
																	<StudentProgressCell
																		key={`reading-${assessmentType.assessmentPeriodId}-${assessmentType.stageId}`}
																		stageName={stageName}
																		width={individualCellWidth}
																		hideLeftLine={isFirst}
																		hideRightLine={isLast}
																	/>
																);
															})}
													</div>

													{/* Spelling */}
													<div
														className={`assessment-student-group ${zebraStriped}`}
														style={{ width: `${assessmentGroupWidth}px` }}
													>
														{student?.spelling &&
															student.spelling.length > 0 &&
															student.spelling.map((assessmentType, assessmentTypeIndex) => {
																const isFirst = assessmentTypeIndex === 0;
																const isLast = assessmentTypeIndex === (student.spelling?.length || 0) - 1;

																const stageId = assessmentType.stageId;
																const stageName = stages.find((stage) => stage.id === stageId)?.shortName || '-';

																return (
																	<StudentProgressCell
																		key={`spelling-${assessmentType.assessmentPeriodId}-${assessmentType.stageId}`}
																		stageName={stageName}
																		width={individualCellWidth}
																		hideLeftLine={isFirst}
																		hideRightLine={isLast}
																	/>
																);
															})}
													</div>
												</div>
											);
										})}
									</div>
								) : null}
							</ScrollContainer>
						</div>
					</div>

					{totalPages > 1 && (
						<div className="mt-5 d-flex justify-content-between align-items-center">
							<PaginationCustom
								pageNumber={assessmentStudentProgressParams.pageNumber || 1}
								totalPages={totalPages}
								pageSwitchCallback={(newPageNumber) => {
									setAssessmentStudentProgressParams({
										...assessmentStudentProgressParams,
										pageNumber: newPageNumber
									});
								}}
							/>

							<div className="body-small">
								<strong>
									Showing {itemsPerPage * (currentPage - 1) + 1} -{' '}
									{currentPage < totalPages ? itemsPerPage * currentPage : totalItems} of {totalItems}{' '}
									{totalItems === 1 ? 'student' : 'students'}
								</strong>
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default StudentProgressTable;
